import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import { supplierService, CreateSupplierRequest } from '../../services/supplierService';
import { companySettingsService } from '../../services/companySettingsService';
import { generateSupplierCode } from '../../utils/codeGenerator';
import '../../styles/ImsTheme.css';
import '../Products/EntityCreatePage.css';
import { parseApiError } from '../../utils/errorUtils';
import { ArrowLeft, Save } from 'lucide-react';

const MAX_LENGTHS = {
  name: 150,
  contactEmail: 150,
  contactPhone: 100,
  address: 500,
  city: 100,
  state: 100,
  postalCode: 50,
  country: 100,
};

interface FieldErrors {
  code?: string;
  name?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  creditPeriodDays?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PAYMENT_TERMS_CASH = 'Cash';
const PAYMENT_TERMS_CREDIT = 'Credit';
const MAX_CREDIT_PERIOD_DAYS = 365;

const SupplierCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [enableAutoSupplierCode, setEnableAutoSupplierCode] = useState(true);
  const [formData, setFormData] = useState<CreateSupplierRequest>({
    code: '',
    name: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    paymentTerms: '',
    creditPeriodDays: 0,
  });
  const [creditDaysInput, setCreditDaysInput] = useState('');

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await companySettingsService.getCompanySettings();
        const settings = response.data?.data;
        const autoGenerate = settings?.enableAutoSupplierCode ?? true;
        setEnableAutoSupplierCode(autoGenerate);

        if (autoGenerate) {
          setFormData((prev) => ({ ...prev, code: generateSupplierCode() }));
        }
      } catch {
        // Default to auto-generation if settings fail to load
        setEnableAutoSupplierCode(true);
        setFormData((prev) => ({ ...prev, code: generateSupplierCode() }));
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    // Prevent changing auto-generated code
    if (name === 'code' && enableAutoSupplierCode) {
      return;
    }

    if (name === 'paymentTerms') {
      setFormData((prev) => ({
        ...prev,
        paymentTerms: value,
        creditPeriodDays: value === PAYMENT_TERMS_CREDIT ? prev.creditPeriodDays : 0,
      }));
      if (value !== PAYMENT_TERMS_CREDIT) setCreditDaysInput('');
      setFieldErrors((prev) => ({ ...prev, creditPeriodDays: undefined }));
      return;
    }

    if (name === 'creditPeriodDays') {
      // Integer only — strip anything that is not a digit
      const digits = value.replace(/\D/g, '');
      setCreditDaysInput(digits);
      setFormData((prev) => ({ ...prev, creditPeriodDays: digits ? parseInt(digits, 10) : 0 }));
      setFieldErrors((prev) => ({ ...prev, creditPeriodDays: undefined }));
      return;
    }

    let finalValue = value;
    if (name in MAX_LENGTHS && value.length > MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]) {
      finalValue = value.substring(0, MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]);
    }

    setFormData((prev) => ({ ...prev, [name]: finalValue }));
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    // Supplier code — required if not auto-generating
    if (!enableAutoSupplierCode) {
      if (!formData.code.trim()) {
        errors.code = t('supplier.formValidation.codeRequired');
        isValid = false;
      } else if (formData.code.length < 3) {
        errors.code = t('supplier.formValidation.codeMinLength');
        isValid = false;
      }
    }

    // Name — required + max length
    if (!formData.name.trim()) {
      errors.name = t('supplier.formValidation.nameRequired');
      isValid = false;
    } else if (formData.name.length > MAX_LENGTHS.name) {
      errors.name = t('supplier.formValidation.nameMaxLength');
      isValid = false;
    }

    // Contact email — required + format + max length
    if (!formData.contactEmail.trim()) {
      errors.contactEmail = t('supplier.formValidation.emailRequired');
      isValid = false;
    } else if (!EMAIL_REGEX.test(formData.contactEmail)) {
      errors.contactEmail = t('supplier.formValidation.emailInvalid');
      isValid = false;
    } else if (formData.contactEmail.length > MAX_LENGTHS.contactEmail) {
      errors.contactEmail = t('supplier.formValidation.emailMaxLength');
      isValid = false;
    }

    // Contact phone — optional, max length only
    if (formData.contactPhone && formData.contactPhone.length > MAX_LENGTHS.contactPhone) {
      errors.contactPhone = t('supplier.formValidation.phoneMaxLength');
      isValid = false;
    }

    // Address — optional, max length only
    if (formData.address && formData.address.length > MAX_LENGTHS.address) {
      errors.address = t('supplier.formValidation.addressMaxLength');
      isValid = false;
    }

    // City — optional, max length only
    if (formData.city && formData.city.length > MAX_LENGTHS.city) {
      errors.city = t('supplier.formValidation.cityMaxLength');
      isValid = false;
    }

    // State — optional, max length only
    if (formData.state && formData.state.length > MAX_LENGTHS.state) {
      errors.state = t('supplier.formValidation.stateMaxLength');
      isValid = false;
    }

    // Postal code — optional, max length only
    if (formData.postalCode && formData.postalCode.length > MAX_LENGTHS.postalCode) {
      errors.postalCode = t('supplier.formValidation.postalCodeMaxLength');
      isValid = false;
    }

    // Country — optional, max length only
    if (formData.country && formData.country.length > MAX_LENGTHS.country) {
      errors.country = t('supplier.formValidation.countryMaxLength');
      isValid = false;
    }

    // Credit period days — required integer when payment terms is Credit
    if (formData.paymentTerms === PAYMENT_TERMS_CREDIT) {
      if (!creditDaysInput || !formData.creditPeriodDays || formData.creditPeriodDays < 1) {
        errors.creditPeriodDays = t('supplier.formValidation.creditDaysRequired');
        isValid = false;
      } else if (formData.creditPeriodDays > MAX_CREDIT_PERIOD_DAYS) {
        errors.creditPeriodDays = t('supplier.formValidation.creditDaysMax', { max: MAX_CREDIT_PERIOD_DAYS });
        isValid = false;
      }
    }

    setFieldErrors(errors);

    if (!isValid) {
      const firstError = Object.values(errors).find((e) => e);
      if (firstError) setError(firstError);
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    if (!validateForm()) return;

    // Check for duplicate code if not auto-generating
    if (!enableAutoSupplierCode) {
      try {
        const response = await supplierService.checkSupplierCodeExists(formData.code);
        if (response.data?.data?.exists) {
          setFieldErrors({ code: t('supplier.formValidation.codeDuplicate') });
          setError(t('supplier.formValidation.codeDuplicate'));
          return;
        }
      } catch {
        // Continue with submission even if check fails
      }
    }

    setSaving(true);
    try {
      await supplierService.createSupplier(formData);
      navigate('/suppliers', { state: { message: t('supplier.supplierCreatedSuccess') } });
    } catch (err: unknown) {
      setError(parseApiError(err, t('supplier.failedToCreateSupplier')).message);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="create-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="create-container">
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/suppliers')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">{t('supplier.createTitle')}</div>
          </div>
          <div className="ims-top-bar-right">
            <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate('/suppliers')} disabled={saving}>
              {t('common.cancel')}
            </button>
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={saving}>
              {saving ? <><span className="ims-btn-spinner" />{t('common.saving')}</> : <><Save size={15} strokeWidth={2} />{t('supplier.createTitle')}</>}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <div className="create-content">
          <div className="create-main-section">
            <div className="create-card">
              <div className="create-card-header">
                <h2 className="create-card-title">{t('supplier.supplierInfo')}</h2>
              </div>
              <div className="create-card-body">
                <form id="page-form" onSubmit={handleSubmit} noValidate>

                  {/* Supplier Code */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('supplier.supplierCode')}
                      {!enableAutoSupplierCode && <span className="required">*</span>}
                      {enableAutoSupplierCode && <span className="badge" style={{ marginLeft: '8px', padding: '2px 8px', fontSize: '11px', backgroundColor: '#dbeafe', color: '#1e40af' }}>Auto</span>}
                    </label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.code ? 'is-invalid' : ''}`}
                      name="code"
                      value={formData.code}
                      onChange={handleInputChange}
                      disabled={enableAutoSupplierCode}
                      placeholder={enableAutoSupplierCode ? t('supplier.codeAutoGenerated') : t('supplier.codeEnterManually')}
                      maxLength={50}
                    />
                    {fieldErrors.code && <small className="form-error">{fieldErrors.code}</small>}
                    {!fieldErrors.code && enableAutoSupplierCode && <small className="form-helper">{t('supplier.codeAutoGeneratedHint')}</small>}
                    {!fieldErrors.code && !enableAutoSupplierCode && <small className="form-helper">{t('supplier.codeManualHint')}</small>}
                  </div>

                  {/* Supplier Name */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('supplier.supplierName')} <span className="required">*</span>
                      <span className="char-counter">{formData.name.length}/{MAX_LENGTHS.name}</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.name ? 'is-invalid' : ''}`}
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.name}
                    />
                    {fieldErrors.name && <small className="form-error">{fieldErrors.name}</small>}
                    {!fieldErrors.name && <small className="form-helper">{t('supplier.nameHint')}</small>}
                  </div>

                  {/* Contact Email */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('supplier.contactEmail')} <span className="required">*</span>
                      <span className="char-counter">{formData.contactEmail.length}/{MAX_LENGTHS.contactEmail}</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.contactEmail ? 'is-invalid' : ''}`}
                      name="contactEmail"
                      value={formData.contactEmail}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.contactEmail}
                    />
                    {fieldErrors.contactEmail && <small className="form-error">{fieldErrors.contactEmail}</small>}
                    {!fieldErrors.contactEmail && <small className="form-helper">{t('supplier.contactEmailHint')}</small>}
                  </div>

                  {/* Contact Phone */}
                  <div className="form-group">
                    <label className="form-label">{t('supplier.contactPhone')}</label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.contactPhone ? 'is-invalid' : ''}`}
                      name="contactPhone"
                      value={formData.contactPhone || ''}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.contactPhone}
                    />
                    {fieldErrors.contactPhone && <small className="form-error">{fieldErrors.contactPhone}</small>}
                    {!fieldErrors.contactPhone && <small className="form-helper">{t('supplier.contactPhoneHint')}</small>}
                  </div>

                  {/* Address */}
                  <div className="form-group">
                    <label className="form-label">{t('supplier.address')}</label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.address ? 'is-invalid' : ''}`}
                      name="address"
                      value={formData.address || ''}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.address}
                    />
                    {fieldErrors.address && <small className="form-error">{fieldErrors.address}</small>}
                    {!fieldErrors.address && <small className="form-helper">{t('supplier.addressHint')}</small>}
                  </div>

                  {/* City */}
                  <div className="form-group">
                    <label className="form-label">{t('supplier.city')}</label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.city ? 'is-invalid' : ''}`}
                      name="city"
                      value={formData.city || ''}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.city}
                    />
                    {fieldErrors.city && <small className="form-error">{fieldErrors.city}</small>}
                    {!fieldErrors.city && <small className="form-helper">{t('supplier.cityHint')}</small>}
                  </div>

                  {/* State / Province */}
                  <div className="form-group">
                    <label className="form-label">{t('supplier.state')}</label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.state ? 'is-invalid' : ''}`}
                      name="state"
                      value={formData.state || ''}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.state}
                    />
                    {fieldErrors.state && <small className="form-error">{fieldErrors.state}</small>}
                    {!fieldErrors.state && <small className="form-helper">{t('supplier.stateHint')}</small>}
                  </div>

                  {/* Postal Code */}
                  <div className="form-group">
                    <label className="form-label">{t('supplier.postalCode')}</label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.postalCode ? 'is-invalid' : ''}`}
                      name="postalCode"
                      value={formData.postalCode || ''}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.postalCode}
                    />
                    {fieldErrors.postalCode && <small className="form-error">{fieldErrors.postalCode}</small>}
                    {!fieldErrors.postalCode && <small className="form-helper">{t('supplier.postalCodeHint')}</small>}
                  </div>

                  {/* Country */}
                  <div className="form-group">
                    <label className="form-label">{t('supplier.country')}</label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.country ? 'is-invalid' : ''}`}
                      name="country"
                      value={formData.country || ''}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.country}
                    />
                    {fieldErrors.country && <small className="form-error">{fieldErrors.country}</small>}
                    {!fieldErrors.country && <small className="form-helper">{t('supplier.countryHint')}</small>}
                  </div>

                  {/* Payment Terms */}
                  <div className="form-group">
                    <label className="form-label">{t('supplier.paymentTerms')}</label>
                    <select
                      className="form-select"
                      name="paymentTerms"
                      value={formData.paymentTerms || ''}
                      onChange={handleInputChange}
                    >
                      <option value="">{t('supplier.paymentTermsSelect')}</option>
                      <option value={PAYMENT_TERMS_CASH}>{t('supplier.paymentTermsCash')}</option>
                      <option value={PAYMENT_TERMS_CREDIT}>{t('supplier.paymentTermsCredit')}</option>
                    </select>
                    <small className="form-helper">{t('supplier.paymentTermsHint')}</small>
                  </div>

                  {/* Credit Period Days — only for Credit terms */}
                  {formData.paymentTerms === PAYMENT_TERMS_CREDIT && (
                    <div className="form-group">
                      <label className="form-label">
                        {t('supplier.creditPeriodDays')} <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        className={`form-input ${fieldErrors.creditPeriodDays ? 'is-invalid' : ''}`}
                        name="creditPeriodDays"
                        value={creditDaysInput}
                        onChange={handleInputChange}
                        placeholder={t('supplier.creditPeriodDaysPlaceholder')}
                        maxLength={3}
                      />
                      {fieldErrors.creditPeriodDays && <small className="form-error">{fieldErrors.creditPeriodDays}</small>}
                      {!fieldErrors.creditPeriodDays && <small className="form-helper">{t('supplier.creditPeriodDaysHint')}</small>}
                    </div>
                  )}

                </form>
              </div>
            </div>
          </div>

          <div className="create-sidebar">
            <div className="create-card">
              <div className="create-card-header">
                <h3 className="create-card-title">{t('supplier.requiredFields')}</h3>
              </div>
              <div className="create-card-body">
                <div className="create-help-section">
                  <ul className="create-help-list">
                    <li>{t('supplier.supplierName')}</li>
                    <li>{t('supplier.contactEmail')}</li>
                  </ul>
                  <hr className="create-divider" />
                  <strong>{t('supplier.optionalFields')}</strong>
                  <ul className="create-help-list">
                    <li>{t('supplier.contactPhone')}</li>
                    <li>{t('supplier.address')}</li>
                    <li>{t('supplier.city')}</li>
                    <li>{t('supplier.state')}</li>
                    <li>{t('supplier.postalCode')}</li>
                    <li>{t('supplier.country')}</li>
                    <li>{t('supplier.paymentTerms')}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SupplierCreatePage;
